import React, { useEffect, useState } from "react";
import { Plus, Trash2, Ticket, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { PageTitle, TextInput, Toggle } from "./shared";

const TYPE_META = {
  fixed: { label: "STORE CREDIT ($)", chip: "bg-forest text-cream" },
  percent: { label: "% OFF", chip: "bg-magenta text-cream" },
  free_shipping: { label: "FREE SHIPPING", chip: "bg-lemon text-plum-deep" },
  free_tags: { label: "FREE TAGS", chip: "bg-plum text-cream" },
};

const randomCode = () => `BLOOM-${Math.random().toString(36).slice(2, 6).toUpperCase()}${Math.floor(10 + Math.random() * 90)}`;

const Vouchers = () => {
  const [vouchers, setVouchers] = useState([]);
  const [form, setForm] = useState({ code: "", type: "fixed", value: "", min_spend: "", max_uses: "", expires_at: "" });

  useEffect(() => {
    api.get("/admin/vouchers").then((r) => setVouchers(r.data)).catch(() => toast.error("Could not load vouchers"));
  }, []);

  const add = async () => {
    if (!form.code.trim()) { toast.error("Give the voucher a code (or hit the wand to generate one)"); return; }
    if (form.type !== "free_shipping" && !Number(form.value)) { toast.error("Set the voucher value"); return; }
    try {
      const { data } = await api.post("/admin/vouchers", {
        code: form.code, type: form.type,
        value: Number(form.value) || 0,
        min_spend: Number(form.min_spend) || 0,
        max_uses: Number(form.max_uses) || 0,
        expires_at: form.expires_at,
      });
      setVouchers((l) => [data, ...l]);
      setForm({ code: "", type: "fixed", value: "", min_spend: "", max_uses: "", expires_at: "" });
      toast.success(`Voucher ${data.code} created`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create the voucher");
    }
  };

  const patch = async (id, body) => {
    try {
      await api.patch(`/admin/vouchers/${id}`, body);
      setVouchers((l) => l.map((v) => (v.id === id ? { ...v, ...body } : v)));
    } catch { toast.error("Could not update the voucher"); }
  };

  const remove = async (v) => {
    if (!window.confirm(`Delete voucher ${v.code}? Customers will no longer be able to use it.`)) return;
    try {
      await api.delete(`/admin/vouchers/${v.id}`);
      setVouchers((l) => l.filter((x) => x.id !== v.id));
      toast.success("Voucher deleted");
    } catch { toast.error("Could not delete the voucher"); }
  };

  return (
    <div data-testid="console-vouchers">
      <PageTitle kicker="PROMOTIONS" title="VOUCHERS" />

      <div className="bg-warmwhite border border-plum-deep/10 rounded-[1.5rem] p-6 mb-8" data-testid="voucher-create-panel">
        <h2 className="font-serif-display uppercase text-plum-deep text-xl mb-2">CREATE A VOUCHER</h2>
        <p className="text-xs text-plum-deep/60 mb-5">Store credit keeps its remaining balance for next time. % off applies to the tag subtotal after quantity discounts. Customers enter the code in their cart.</p>
        <div className="flex flex-wrap gap-3 items-center">
          <div className="flex items-center gap-1 flex-1 min-w-[200px]">
            <TextInput placeholder="CODE — e.g. THANKYOU10" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} data-testid="voucher-code-input" />
            <button onClick={() => setForm({ ...form, code: randomCode() })} title="Generate a code" data-testid="voucher-generate-btn" className="w-10 h-10 shrink-0 rounded-full bg-plum text-cream flex items-center justify-center hover:bg-magenta"><Wand2 className="w-4 h-4" /></button>
          </div>
          <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="voucher-type-select" className="rounded-full px-4 py-2.5 bg-cream border border-plum-deep/15 text-sm focus:outline-none">
            <option value="fixed">STORE CREDIT ($)</option>
            <option value="percent">% OFF</option>
            <option value="free_shipping">FREE SHIPPING</option>
            <option value="free_tags">FREE TAGS (COUNT)</option>
          </select>
          {form.type !== "free_shipping" && (
            <TextInput placeholder={form.type === "fixed" ? "Value $" : "%"} type="number" step="0.5" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} data-testid="voucher-value-input" className="w-24 !px-3" />
          )}
        </div>
        <div className="mt-3 flex flex-wrap gap-3 items-center">
          <label className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.12em] text-plum-deep/50 font-semibold">
            MIN SPEND $
            <TextInput type="number" step="1" value={form.min_spend} onChange={(e) => setForm({ ...form, min_spend: e.target.value })} data-testid="voucher-minspend-input" className="w-20 !px-3" placeholder="0" />
          </label>
          <label className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.12em] text-plum-deep/50 font-semibold">
            MAX USES
            <TextInput type="number" value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })} data-testid="voucher-maxuses-input" className="w-20 !px-3" placeholder="∞" />
          </label>
          <label className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.12em] text-plum-deep/50 font-semibold">
            EXPIRES
            <TextInput type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} data-testid="voucher-expiry-input" className="!px-3" />
          </label>
          <button onClick={add} data-testid="voucher-add-btn" className="inline-flex items-center gap-2 rounded-full bg-magenta text-cream px-6 py-3 text-xs uppercase tracking-[0.16em] font-semibold hover:bg-plum">
            <Plus className="w-4 h-4" /> CREATE VOUCHER
          </button>
        </div>
      </div>

      <div className="space-y-3" data-testid="voucher-list">
        {vouchers.length === 0 && <p className="text-sm text-plum-deep/50">No vouchers yet — create your first code above.</p>}
        {vouchers.map((v) => (
          <div key={v.id} className="bg-warmwhite border border-plum-deep/10 rounded-2xl p-4 flex items-center gap-4 flex-wrap" data-testid={`voucher-row-${v.code}`}>
            <span className="inline-flex items-center gap-2 bg-plum-deep text-lemon rounded-lg px-4 py-2 font-mono tracking-[0.2em] text-sm font-bold">
              <Ticket className="w-4 h-4" /> {v.code}
            </span>
            <span className={`text-[10px] uppercase tracking-[0.14em] font-bold rounded-full px-3 py-1.5 ${TYPE_META[v.type]?.chip}`}>
              {v.type === "fixed" ? `$${Number(v.value).toFixed(2)} CREDIT` : v.type === "percent" ? `${Number(v.value)}% OFF` : v.type === "free_tags" ? `${Number(v.value)} FREE TAGS` : "FREE SHIPPING"}
            </span>
            {v.gift && <span className="text-[10px] uppercase tracking-[0.14em] font-bold rounded-full px-3 py-1.5 bg-lemon/60 text-plum-deep">GIFT → {v.gift.recipient_email}</span>}
            <div className="flex-1 min-w-[180px] text-xs text-plum-deep/60 uppercase tracking-[0.1em] space-x-3">
              {v.type === "fixed" && <span data-testid={`voucher-balance-${v.code}`} className={Number(v.balance) <= 0 ? "text-red-500 font-bold" : "text-forest font-bold"}>BALANCE ${Number(v.balance).toFixed(2)}</span>}
              <span>USED {v.uses}{Number(v.max_uses) ? `/${v.max_uses}` : ""}</span>
              {Number(v.min_spend) > 0 && <span>MIN ${Number(v.min_spend).toFixed(0)}</span>}
              {v.expires_at && <span>EXPIRES {v.expires_at}</span>}
            </div>
            <Toggle checked={v.active} onChange={() => patch(v.id, { active: !v.active })} testid={`voucher-toggle-${v.code}`} />
            <button onClick={() => remove(v)} className="text-plum-deep/40 hover:text-red-600" data-testid={`voucher-delete-${v.code}`} aria-label="Delete voucher">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Vouchers;
